import { useState } from "react";
import { Case, Switch } from "react-if";
import {
  ActionType,
  currencyFormatter,
  MixpanelEvents,
  MixpanelLocationTypes,
  toDecimal,
} from "shared";
import { useParams } from "react-router";
import { FaceSmileIcon } from "@heroicons/react/24/outline";

import { Card } from "../../../../common/Atoms/Card";
import { MultiButtonCardHeader } from "../../../../common/Atoms/MultiButtonCardHeader";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { CardContent } from "../../../../common/Atoms/CardContent";
import { WrappedLink } from "../../WrappedLink";
import { LoadingElementPlaceholder } from "../../../../common/Atoms/LoadingElementPlaceholder";
import { useFetchSlowPayersMetricsQuery } from "../../../services/api/organisationApi/organisation";
import { classNames } from "../../../../common/lib/classNames";
import { mixpanelService } from "../../../../common/lib/mixpanel";
import { useAdhocActionModal } from "../../../hooks/useAdhocActionModal";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";

// Days-overdue severity colors (design 1d)
function daysColor(days: number) {
  if (days >= 90) return `#c11415`;
  if (days >= 8) return `#b45309`;

  return `#6b7280`;
}

export function BadContacts() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const { openBulkStatements, openBulkInterests } = useAdhocActionModal();
  const { data: response, isLoading } = useFetchSlowPayersMetricsQuery({ orgId: organisationId }, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
  });
  const currentOrg = useGetSelectedOrganisation();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const badContacts = response?.contacts || [];
  const formatCurrency = currentOrg ? currencyFormatter(currentOrg.baseCurrency) : () => ``;

  // Count covers ALL overdue contacts, not just the 10 fetched rows
  const overdueContactCount = response?.totalCount || 0;
  const oldestDays = badContacts.reduce((max, c) => Math.max(max, c.oldestDaysOverdue || 0), 0);
  // Filter through the current list so ids stale after a refetch are excluded
  const selectedContacts = badContacts.filter(c => selectedIds.includes(c.id));
  const selectedContactIds = selectedContacts.map(c => c.id);
  const selectedOverdue = selectedContacts.reduce((sum, c) => sum + (c.overdueBalanceHome || 0), 0);

  function toggleSelected(contactId: number) {
    setSelectedIds(ids => ids.includes(contactId) ? ids.filter(id => id !== contactId) : [...ids, contactId]);
  }

  function onBulkStatements() {
    if (!selectedContactIds.length) return;

    openBulkStatements(selectedContactIds);

    mixpanelService.trackMixpanelEvent(MixpanelEvents.BulkAdhocAction, {
      location: MixpanelLocationTypes.Dashboard,
      action_type: ActionType.SEND_STATEMENT,
    });
  }

  function onBulkInterest() {
    if (!selectedContactIds.length) return;

    openBulkInterests(selectedContactIds);

    mixpanelService.trackMixpanelEvent(MixpanelEvents.BulkAdhocAction, {
      location: MixpanelLocationTypes.Dashboard,
      action_type: ActionType.ISSUE_STATEMENT_LATE_FEE,
    });
  }

  return (
    <Card className={ `w-full h-full flex flex-col overflow-hidden` }
      variant={ `soft` }>
      <MultiButtonCardHeader
        title={ `Slowest Payers` }
        description={ `Contacts with the oldest overdue invoices` }
        variant={ `plain` }
      />
      <Switch>
        <Case condition={ isLoading }>
          <CardContent className={ `mt-4` }>
            <LoadingElementPlaceholder
              border={ false }
              rows={ 6 }
            />
          </CardContent>
        </Case>
        <Case condition={ !badContacts.length }>
          <CardContent className={ `mt-4` }>
            <div className={ `min-h-[200px] h-full flex flex-col items-center justify-center text-center` }>
              <FaceSmileIcon className={ `w-12 h-12 mx-auto text-gray-300` } />
              <Paragraph variant={ `secondary` }>
                { `You have no slow payers!` }
              </Paragraph>
            </div>
          </CardContent>
        </Case>
        <Case condition={ badContacts.length }>
          { /* Summary strip (design 1d) */ }
          <div className={ `mt-4 -mx-4 sm:-mx-5 grid grid-cols-2 border-y border-gray-100 bg-[#fafbfc] dark:bg-gray-50` }>
            <div className={ `py-3 px-4 sm:px-5 border-r border-gray-100` }>
              <div className={ `text-[11px] font-semibold tracking-wider text-gray-400 uppercase` }>{ `Contacts overdue` }</div>
              <div className={ `text-lg font-semibold mt-0.5` }>{ overdueContactCount }</div>
            </div>
            <div className={ `py-3 px-4 sm:px-5` }>
              <div className={ `text-[11px] font-semibold tracking-wider text-gray-400 uppercase` }>{ `Oldest invoice` }</div>
              <div className={ `text-lg font-semibold mt-0.5` }>{ oldestDays }{ ` days` }</div>
            </div>
          </div>

          { /* Contact rows */ }
          <div className={ `-mx-4 sm:-mx-5 grow` }>
            { badContacts.map(contact => {
              const selected = selectedIds.includes(contact.id);

              return (
                <div
                  key={ contact.id }
                  className={ classNames(
                    `grid grid-cols-[16px_1fr_90px_110px] gap-x-3 items-center py-3 px-4 sm:px-5 border-b border-gray-100 cursor-pointer`,
                    selected ? `bg-[#f6f9f9] dark:bg-gray-200` : `hover:bg-gray-50`,
                  ) }
                  onClick={ () => toggleSelected(contact.id) }
                >
                  <input
                    type={ `checkbox` }
                    checked={ selected }
                    onClick={ e => e.stopPropagation() }
                    onChange={ () => toggleSelected(contact.id) }
                    className={ `h-4 w-4 rounded-sm border-gray-300 text-paidniceGreen-600 focus:ring-paidniceGreen-600 cursor-pointer` }
                  />
                  <WrappedLink
                    to={ `/contacts/${contact.id}` }
                    onClick={ e => e.stopPropagation() }
                  >
                    <Paragraph
                      variant={ `link` }
                      className={ `truncate` }
                    >
                      { contact.name }
                    </Paragraph>
                  </WrappedLink>
                  <span
                    className={ `text-[13px] font-semibold text-right` }
                    style={ { color: daysColor(contact.oldestDaysOverdue || 0) } }
                  >
                    { contact.oldestDaysOverdue }
                    { ` days` }
                  </span>
                  <span className={ `text-sm font-semibold text-right tabular-nums` }>
                    { formatCurrency(toDecimal(contact.overdueBalanceHome || 0)) }
                  </span>
                </div>
              );
            }) }
          </div>

          { /* Selection action bar */ }
          { selectedContactIds.length > 0 && (
            <div className={ `-mx-4 sm:-mx-5 -mb-5 flex items-center justify-between flex-wrap gap-2 px-4 sm:px-5 py-3 bg-[#2f4e60]` }>
              <span className={ `text-[13px] text-slate-200 dark:text-[#e2e8f0]` }>
                <strong className={ `text-white` }>{ selectedContactIds.length }{ ` selected` }</strong>
                { ` · ` }
                { formatCurrency(toDecimal(selectedOverdue)) }
                { ` overdue` }
              </span>
              <div className={ `flex gap-2.5` }>
                <button
                  type={ `button` }
                  className={ `rounded-lg border border-white/30 bg-white/10 hover:bg-white/20 px-3.5 py-1.5 text-[13px] font-medium text-white` }
                  onClick={ onBulkStatements }
                >
                  { `Send statements` }
                </button>
                <button
                  type={ `button` }
                  className={ `rounded-lg bg-[#e51d1e] hover:bg-[#c11415] px-3.5 py-1.5 text-[13px] font-semibold text-white` }
                  onClick={ onBulkInterest }
                >
                  { `Charge interest` }
                </button>
              </div>
            </div>
          ) }
        </Case>
      </Switch>
    </Card>
  );
}
